import { useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    Alert,
    LinearProgress,
    IconButton,
    Divider
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DeleteIcon from '@mui/icons-material/Delete';
import * as XLSX from 'xlsx';

const FreteGratisImportModal = ({ open, onClose, onConfirm }) => {
    const [files, setFiles] = useState([]);
    const [processedData, setProcessedData] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileSelect = () => {
        fileInputRef.current?.click();
    };

    const validateMLB = (value) => {
        if (!value) return false;
        const stringValue = String(value).trim().toUpperCase();
        return /^MLB\d+$/.test(stringValue);
    };

    const processFile = async (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    
                    let allMLBs = [];
                    let errors = [];

                    workbook.SheetNames.forEach(sheetName => {
                        const worksheet = workbook.Sheets[sheetName];
                        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                        
                        // Encontrar coluna que contém MLBs
                        let mlbColumnIndex = -1;
                        
                        // Procurar na primeira linha (cabeçalho)
                        if (jsonData.length > 0) {
                            const headerRow = jsonData[0];
                            mlbColumnIndex = headerRow.findIndex(header => 
                                header && String(header).toLowerCase().includes('mlb')
                            );
                        }
                        
                        // Se não encontrou no cabeçalho, procurar por dados que parecem MLB
                        if (mlbColumnIndex === -1) {
                            for (let rowIndex = 0; rowIndex < Math.min(jsonData.length, 10); rowIndex++) {
                                const row = jsonData[rowIndex];
                                for (let colIndex = 0; colIndex < row.length; colIndex++) {
                                    if (validateMLB(row[colIndex])) {
                                        mlbColumnIndex = colIndex;
                                        break;
                                    }
                                }
                                if (mlbColumnIndex !== -1) break;
                            }
                        }

                        if (mlbColumnIndex !== -1) {
                            jsonData.slice(1).forEach((row, index) => {
                                const cellValue = row[mlbColumnIndex];
                                if (cellValue) {
                                    const mlb = String(cellValue).trim().toUpperCase();
                                    if (validateMLB(mlb)) {
                                        if (!allMLBs.includes(mlb)) {
                                            allMLBs.push(mlb);
                                        }
                                    } else {
                                        errors.push({
                                            sheet: sheetName,
                                            row: index + 2,
                                            value: cellValue,
                                            reason: 'Formato inválido'
                                        });
                                    }
                                }
                            });
                        } else {
                            errors.push({
                                sheet: sheetName,
                                row: 'N/A',
                                value: 'N/A',
                                reason: 'Coluna MLB não encontrada'
                            });
                        }
                    });

                    resolve({
                        fileName: file.name,
                        mlbs: allMLBs,
                        errors: errors,
                        totalRows: allMLBs.length + errors.length,
                        validMLBs: allMLBs.length,
                        status: errors.length === 0 ? 'success' : 'warning'
                    });
                } catch (error) {
                    resolve({
                        fileName: file.name,
                        mlbs: [],
                        errors: [{ sheet: 'N/A', row: 'N/A', value: 'N/A', reason: 'Erro ao processar arquivo' }],
                        totalRows: 0,
                        validMLBs: 0,
                        status: 'error'
                    });
                }
            };
            reader.readAsArrayBuffer(file);
        });
    };

    const handleFileChange = async (event) => {
        const selectedFiles = Array.from(event.target.files);
        if (selectedFiles.length === 0) return;

        setIsProcessing(true);
        setFiles(prev => [...prev, ...selectedFiles]);

        const newProcessedData = [];
        for (const file of selectedFiles) {
            const result = await processFile(file);
            newProcessedData.push(result);
        }

        setProcessedData(prev => [...prev, ...newProcessedData]);
        setIsProcessing(false);
        
        // Limpar input
        event.target.value = '';
    };

    const handleRemoveFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
        setProcessedData(prev => prev.filter((_, i) => i !== index));
    };

    const handleConfirm = () => {
        const allMLBs = processedData.flatMap(data => data.mlbs);
        const uniqueMLBs = [...new Set(allMLBs)];
        
        onConfirm({ mlbs: uniqueMLBs });
        handleClose();
    };

    const handleClose = () => {
        setFiles([]);
        setProcessedData([]);
        setIsProcessing(false);
        onClose();
    };

    const totalValidMLBs = processedData.reduce((sum, data) => sum + data.validMLBs, 0);
    const totalErrors = processedData.reduce((sum, data) => sum + data.errors.length, 0);
    const hasErrors = totalErrors > 0;
    const canConfirm = totalValidMLBs > 0 && !isProcessing;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{ 
                pb: 1,
                borderBottom: '1px solid #e9ecef'
            }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                    Importar MLBs de Planilhas
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Selecione arquivos Excel (.xlsx, .xls) contendo códigos MLB
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ py: 3 }}>
                {/* Área de upload */}
                <Box
                    onClick={handleFileSelect}
                    sx={{
                        border: '2px dashed #dee2e6',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        bgcolor: '#f8f9fa',
                        '&:hover': {
                            borderColor: '#adb5bd',
                            bgcolor: '#e9ecef'
                        },
                        mb: 3
                    }}
                >
                    <UploadFileIcon sx={{ fontSize: 48, color: '#6c757d', mb: 1 }} />
                    <Typography variant="h6" sx={{ color: '#495057', mb: 0.5 }}>
                        Clique para selecionar arquivos
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Arquivos Excel (.xlsx, .xls) - Múltipla seleção permitida
                    </Typography>
                </Box>

                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />

                {/* Progress bar */}
                {isProcessing && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Processando arquivos...
                        </Typography>
                        <LinearProgress />
                    </Box>
                )}

                {/* Resumo */}
                {processedData.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                            Resumo da Importação
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Chip
                                label={`${totalValidMLBs} MLBs válidos`}
                                color="success"
                                size="small"
                            />
                            {hasErrors && (
                                <Chip
                                    label={`${totalErrors} erros`}
                                    color="error"
                                    size="small"
                                />
                            )}
                        </Box>

                        {hasErrors && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Alguns registros contêm erros. Apenas os MLBs válidos serão importados.
                            </Alert>
                        )}
                    </Box>
                )}

                {/* Lista de arquivos processados */}
                {processedData.length > 0 && (
                    <List sx={{ bgcolor: '#f8f9fa', borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                        {processedData.map((data, index) => (
                            <ListItem key={index} sx={{ py: 1 }}>
                                <ListItemIcon>
                                    {data.status === 'success' ? (
                                        <CheckCircleIcon color="success" />
                                    ) : data.status === 'warning' ? (
                                        <ErrorIcon color="warning" />
                                    ) : (
                                        <ErrorIcon color="error" />
                                    )}
                                </ListItemIcon>
                                <ListItemText
                                    primary={data.fileName}
                                    secondary={
                                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                            <Chip
                                                label={`${data.validMLBs} válidos`}
                                                size="small"
                                                variant="outlined"
                                                color="success"
                                            />
                                            {data.errors.length > 0 && (
                                                <Chip
                                                    label={`${data.errors.length} erros`}
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                />
                                            )}
                                        </Box>
                                    }
                                />
                                <IconButton
                                    edge="end"
                                    onClick={() => handleRemoveFile(index)}
                                    size="small"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                        borderColor: '#dee2e6',
                        color: '#495057'
                    }}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={!canConfirm}
                    sx={{
                        bgcolor: '#198754',
                        '&:hover': { bgcolor: '#157347' }
                    }}
                >
                    Importar {totalValidMLBs > 0 ? `${totalValidMLBs} MLBs` : ''}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FreteGratisImportModal;